import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, useLocation, NavLink } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  ButtonSecondary,
  IconExpand,
  ajvResolver,
  ButtonPrimary,
  ButtonPseudo,
  SidePanelLoader,
  SidePanelType,
  Popup,
  IconTrash,
  ResponseError,
  responseErrorsResolver,
  PermissionDenied,
  IconChangeLoClock,
  getResponseErrors,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearAgreementState } from './store/slices/agreement-item';
import Form from '../../components/settings/agreement-form/form';
import {
  Agreement,
  AgreementFormValues,
  AgreementStatus,
  UploadResponse,
} from './types';
import {
  useLazyGetAgreementQuery,
  useEmployeesUploadMutation,
  useLazyGetEmployeesAgreementFileQuery,
  useAddAgreementMutation,
  useLazyGetAgreementsQuery,
  useChangeAgreementsStatusMutation,
} from './store/services/agreements';
import { getAgreementTypeName } from './helpers/format-agreements-list';
import { PermissionEnum } from '../user-roles/types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const schema = {
  type: 'object',
  properties: {
    uploadId: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Attachment file is required',
      },
    },
    startAt: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Date applied is required',
      },
    },
    client: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Customer type is required',
      },
    },
    type: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Type is required',
      },
    },
    version: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Version is required',
      },
    },
  },
  required: ['uploadId', 'startAt', 'client', 'type', 'version'],
};

const AgreementForm = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<
    | {
        name?: string;
        id?: string;
      }
    | undefined
  >();
  const [link, setLink] = useState('');
  const [defaultFormVaues, setDefaultFormValues] =
    useState<AgreementFormValues>({
      client: undefined,
      description: '',
      startAt: '',
      type: undefined,
      version: undefined,
    });
  const [archivePopupVisibile, setArchivePopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [uploadedFilesErrors, setUploadedFilesErrors] = useState<string[]>([]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm<AgreementFormValues>({
    resolver: ajvResolver(schema),
    defaultValues: {
      client: undefined,
      description: '',
      startAt: '',
      type: undefined,
      version: '',
    },
  });

  const fields = watch();

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const [upload, { isLoading: fileUploadInProgress }] =
    useEmployeesUploadMutation();
  const [
    createAgreement,
    { isLoading: requestInProgress, isSuccess: submitSuccess },
  ] = useAddAgreementMutation();
  const [
    changeAgreementStatus,
    { isLoading: statusChangeInProgress, isSuccess: archivateIsSuccess },
  ] = useChangeAgreementsStatusMutation();

  const {
    settings: {
      agreements: {
        form: { agreement },
        list: { takeCount },
      },
    },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const isDirty =
    activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST) &&
    activePermissions.includes(PermissionEnum.AGREEMENTS_CREATE_ACTION) &&
    !isEqual(fields, defaultFormVaues);

  const [getAgreement, { isLoading, isFetching }] = useLazyGetAgreementQuery();
  const [getFile] = useLazyGetEmployeesAgreementFileQuery();
  const [getAgreements] = useLazyGetAgreementsQuery();

  const { agreementId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const getAgreementFile = async (id: string) => {
    const response = await getFile(id);

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setLink(blob);
    }
  };

  const onStatusChange = async () => {
    const response = (await changeAgreementStatus([agreementId as string])) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError,
      });
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await createAgreement({
      type: values.type,
      client: values.client,
      uploadId: uploadedFile?.id,
      description: values.description,
      version: Number(values.version),
      startAt: values.startAt ? new Date(values.startAt).toISOString() : '',
    })) as { data?: Agreement; error?: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setGlobalError('');

    setTimeout(() => {
      navigate(`${ROUTES.AGREEMENTS_LIST}${search || ''}`);
    }, 100);
  };

  const onUpload = async (files: FileList) => {
    setUploadedFilesErrors([]);

    const nextUploadedFilesErrors: string[] = [];

    const responseFiles = await Promise.all(
      Object.keys(files).map(async (file) => {
        const response = (await upload(files[file as any])) as unknown as {
          data?: UploadResponse[];
          error?: {
            originalStatus: number;
          };
        };

        let nextFile;

        if (response && response.data) {
          nextFile = { ...response.data[0] };

          let fileName = files[file as any]?.name;

          if (
            response.data[0] &&
            response.data[0]?.mimeType?.includes('image')
          ) {
            fileName = `${files[file as any]?.name.slice(0, files[file as any]?.name.lastIndexOf('.'))}.jpg`;
          }

          nextFile.name = fileName;
        } else if (response.error && response.error?.originalStatus === 413) {
          nextUploadedFilesErrors.push(
            `${files[file as any].name} size is too large`,
          );
        }

        return nextFile;
      }),
    );

    setUploadedFilesErrors(nextUploadedFilesErrors);

    if (responseFiles && responseFiles.length) {
      responseFiles.forEach((item) => {
        if (item && item.name && item.id) {
          setUploadedFile({
            name: item.name,
            id: item.id,
          });

          setValue('uploadId', item.id);
        }
      });
    }

    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }
  };

  const getAgreementAction = useCallback(async (id: string) => {
    const response = (await getAgreement(id)) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList: setGlobalError,
      });
    }
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);

    if (
      agreementId &&
      activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST)
    ) {
      getAgreementAction(agreementId);
      getAgreementFile(agreementId);
    }

    return () => {
      dispatch(clearAgreementState());
    };
  }, [dispatch, agreementId, activePermissions]);

  useEffect(() => {
    if (agreement) {
      reset({
        type: agreement.type,
        client: agreement.client,
        uploadId: agreement.uploadId,
        description: agreement.description,
        version: agreement?.version?.toString(),
        startAt: agreement.startAt,
      });

      setDefaultFormValues({
        type: agreement.type,
        client: agreement.client,
        uploadId: agreement.uploadId,
        description: agreement.description,
        version: agreement?.version?.toString(),
        startAt: agreement.startAt,
      });
    }
  }, [agreement]);

  useEffect(() => {
    if (submitSuccess) {
      handleClose();

      getAgreements(
        `?take=${((takeCount as number) + 1)?.toString()}${search.replace(
          '?',
          '&',
        )}`,
      );
    }
  }, [submitSuccess]);

  useEffect(() => {
    if (archivateIsSuccess) {
      handleClose();
    }
  }, [archivateIsSuccess]);

  useEffect(() => {
    let title = 'Settings: Agreements';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (
    (agreementId &&
      !activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST)) ||
    (!agreementId &&
      (!activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST) ||
        !activePermissions.includes(PermissionEnum.AGREEMENTS_CREATE_ACTION)))
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        <div className={cn.agreenentFormControlsWrapper}>
          {agreementId && agreement?.status !== AgreementStatus.ARCHIVED && (
            <div className={cn.agreenentFormControls}>
              {activePermissions?.includes(
                PermissionEnum.AGREEMENTS_ARCHIVE_ACTION,
              ) && (
                <ButtonSecondary
                  className={`button-white ${cn.agreenentFormControlButton}`}
                  text="Archive"
                  onClick={() => setArchivePopupVisible(true)}
                  bold
                />
              )}

              <ButtonSecondary
                className={`button-white ${cn.agreenentFormControlButton}`}
                text="Notify customers"
                bold
              />
            </div>
          )}
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
            agreementId && (
              <div className={cn.changeLogLinkWrapper}>
                <NavLink
                  to={`/administration/change-logs?entityType=AGREEMENT&entityId=${agreementId}`}
                  className={cn.changeLogLink}
                  target="_blank"
                >
                  <IconChangeLoClock />
                  Change Log
                </NavLink>
              </div>
            )}
        </div>

        {!agreementId && !uploadedFile && (
          <div className={cn.inputTypeFileWrapper}>
            {/*  eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="file-input">
              <input
                ref={uploadInputRef}
                type="file"
                className={cn.inputFile}
                id="file-input"
                onChange={(e) => onUpload(e.target.files as FileList)}
              />
            </label>
            <div className={cn.inputFileVisual}>
              <span>
                {!fileUploadInProgress && <IconExpand />}
                {fileUploadInProgress ? 'Loading...' : 'Add PDF'}
              </span>
            </div>
          </div>
        )}
        {(uploadedFile || agreement?.uploadId) && (
          <div className={cn.file}>
            <div
              className={`${cn.fileName} ${
                agreementId ? cn.fileNameFullWidth : ''
              }`}
            >
              {(fileUploadInProgress || (agreementId && !link)) && 'Loading...'}
              {agreement && link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                >{`${getAgreementTypeName(
                  agreement?.type,
                )} (v${agreement.version})`}</a>
              )}
              {!agreementId && uploadedFile?.name}
            </div>
            {!agreementId && (
              <div className={cn.deleteFileButtonWrapper}>
                <button
                  type="button"
                  className={cn.deleteFileButton}
                  onClick={() => setUploadedFile(undefined)}
                  aria-label="Delete"
                >
                  <IconTrash />
                </button>
              </div>
            )}
          </div>
        )}
        {errors?.uploadId?.message && (
          <div className={cn.error}>{errors?.uploadId?.message}</div>
        )}
        {uploadedFilesErrors && uploadedFilesErrors.length > 0 && (
          <ul className={cn.uploadedFilesErrors}>
            {uploadedFilesErrors.map((item) => (
              <li key={item}>{`- ${item}`}</li>
            ))}
          </ul>
        )}
        <div className={cn.agreementFormSeparator} />
        <Form control={control} errors={errors} agreementId={agreementId} />
      </>
    );
  }

  let title;

  if (agreementId && agreement) {
    title = getAgreementTypeName(agreement?.type);
  } else {
    title = 'Add new agreements';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={globalError}
        sidepanelExpandedKey="agreement-form"
        footer={
          <>
            {!agreementId &&
              activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST) &&
              activePermissions?.includes(
                PermissionEnum.AGREEMENTS_CREATE_ACTION,
              ) && (
                <ButtonPrimary
                  text={requestInProgress ? 'Loading...' : 'Add agreement'}
                  onClick={onSubmit}
                  bold
                />
              )}

            <ButtonPseudo
              text="Close"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title={`Archivate ${title} agreement?`}
        onCancelClick={() => setArchivePopupVisible(false)}
        onOkClick={onStatusChange}
        cancelButtonText="Cancel"
        okButtonText="Yes, archivate"
        isVisible={archivePopupVisibile}
        parent={document.getElementById('archive-popup') as HTMLElement}
        isLoading={statusChangeInProgress}
      />
      <div id="dirty-popup" />
      <div id="archive-popup" />
    </>
  );
};

export default AgreementForm;
